"use client"

import { create } from "zustand"
import type { BooruPost } from "./booru-api"

interface ScriptLinkingState {
  isLinkingMode: boolean
  pendingPost: BooruPost | null
  returnToEditor: boolean
  returnToTab?: "new-script" | "library" | "vertical-editor" | "story-scripter" | "curve-editor" | "block-editor" | "manual-control" | "patterns" | "playmate" | "script-player" | "global-script-player" | "bounties" | "commissions" | "Gooncave"
  /**
   * Which Gooncave grid cell the picked post should be placed into on return.
   * Gooncave writes this (and returnToTab: "Gooncave") via setState and reads it
   * back after the picker closes. Both were absent from this type — zustand's
   * setState merges unknown keys rather than stripping them, so the flow worked
   * at runtime while being invisible to TypeScript.
   */
  linkingCellIndex?: number | null
  preferredTab?: "curve" | "block" | "manual"
  // Commission/bounty context — when set, Save in editor delivers to this commission/bounty
  commissionId?: string | null
  commissionTitle?: string | null
  wipScript?: string | null   // existing WIP content to resume from
  bountyId?: string | null
  bountyTitle?: string | null
  workScriptId?: string | null

  startLinking: (target?: "new-script" | "vertical-editor" | "story-scripter" | "curve-editor" | "block-editor" | "manual-control" | "patterns" | "playmate" | "script-player" | "global-script-player" | "bounties" | "commissions") => void
  startCommissionScripting: (commissionId: string, post: BooruPost, editor?: "vertical-editor" | "block-editor" | "curve-editor" | "manual-control" | "story-scripter", commissionTitle?: string, wipScript?: string | null, workScriptId?: string | null) => void
  startBountyScripting: (bountyId: string, post: BooruPost, editor?: "vertical-editor" | "block-editor" | "curve-editor" | "manual-control" | "story-scripter", bountyTitle?: string, workScriptId?: string | null) => void
  selectPostForScript: (post: BooruPost, preferredTab?: "curve" | "block" | "manual") => void
  setEditorTarget: (tab: "vertical-editor" | "story-scripter" | "curve-editor" | "block-editor" | "manual-control") => void
  // Shows/hides the commission/bounty banner (Save Draft / Submit Script) based on the script
  // actually being opened, regardless of how the user navigated to it (e.g. via My Scripts) —
  // without this, a commission/bounty-linked script opened outside its own dashboard loses the
  // banner and the user is pushed toward the regular Save/Publish flow instead.
  setOrderContext: (ctx: { commissionId?: string | null; commissionTitle?: string | null; bountyId?: string | null; bountyTitle?: string | null }) => void
  clearLinking: () => void
  getPendingPost: () => BooruPost | null
  consumePendingPost: () => BooruPost | null
}

export const useScriptLinkingStore = create<ScriptLinkingState>((set, get) => ({
  isLinkingMode: false,
  pendingPost: null,
  returnToEditor: false,
  returnToTab: "library",
  preferredTab: undefined,
  linkingCellIndex: null,
  commissionId: null,
  commissionTitle: null,
  wipScript: null,
  bountyId: null,
  bountyTitle: null,
  workScriptId: null,

  startLinking: (target = "new-script") => {
    set({ isLinkingMode: true, pendingPost: null, returnToEditor: false, returnToTab: target, preferredTab: undefined, commissionId: null, bountyId: null, workScriptId: null })
  },

  // Called from commission/bounty detail — pre-loads content and routes to the chosen editor
  startCommissionScripting: (commissionId: string, post: BooruPost, editor: "vertical-editor" | "block-editor" | "curve-editor" | "manual-control" | "story-scripter" = "curve-editor", commissionTitle?: string, wipScript?: string | null, workScriptId?: string | null) => {
    const preferredTab = editor === "curve-editor" ? "curve" : editor === "block-editor" ? "block" : editor === "manual-control" ? "manual" : undefined
    set({
      isLinkingMode: false,
      pendingPost: workScriptId ? null : post,
      returnToEditor: true,
      returnToTab: editor,
      preferredTab,
      commissionId,
      commissionTitle: commissionTitle ?? null,
      wipScript: wipScript ?? null,
      bountyId: null,
      workScriptId: workScriptId ?? null,
    })
  },

  startBountyScripting: (bountyId: string, post: BooruPost, editor: "vertical-editor" | "block-editor" | "curve-editor" | "manual-control" | "story-scripter" = "curve-editor", bountyTitle?: string, workScriptId?: string | null) => {
    const preferredTab = editor === "curve-editor" ? "curve" : editor === "block-editor" ? "block" : editor === "manual-control" ? "manual" : undefined
    set({
      isLinkingMode: false,
      pendingPost: workScriptId ? null : post,
      returnToEditor: true,
      returnToTab: editor,
      preferredTab,
      commissionId: null,
      commissionTitle: null,
      wipScript: null,
      bountyId,
      bountyTitle: bountyTitle ?? null,
      workScriptId: workScriptId ?? null,
    })
  },

  setOrderContext: (ctx) => {
    set({
      commissionId: ctx.commissionId ?? null,
      commissionTitle: ctx.commissionTitle ?? null,
      bountyId: ctx.bountyId ?? null,
      bountyTitle: ctx.bountyTitle ?? null,
    })
  },

  selectPostForScript: (post: BooruPost, preferredTab?: "curve" | "block" | "manual") => {
    const { isLinkingMode, commissionId, bountyId, returnToTab: currentReturnTab } = get()
    // "Gooncave" belongs here even though it isn't an editor: it's a destination that owns
    // its own return routing. Leaving it out silently rewrote it to "new-script", so picking
    // a post for a Gooncave slot dumped the user in the Editors Hub "Choose an Editor" picker
    // and the slot never got filled.
    const specificTargets = ["vertical-editor", "story-scripter", "curve-editor", "block-editor", "manual-control", "patterns", "playmate", "script-player", "global-script-player", "Gooncave"]
    // Only bypass the picker when the user was explicitly in linking mode (they opened an editor
    // and are now picking content for it) or in a commission scripting context.
    // A plain "Create Script" press from the post detail must always show the picker —
    // never use a stale returnToTab from a previous editor selection.
    const targetTab =
      (isLinkingMode || !!commissionId || !!bountyId) && specificTargets.includes(currentReturnTab as string)
        ? currentReturnTab
        : "new-script"
    set({ pendingPost: post, returnToEditor: true, isLinkingMode: false, returnToTab: targetTab, preferredTab })
  },

  setEditorTarget: (tab) => {
    set({ returnToTab: tab })
  },

  clearLinking: () => {
    set({ isLinkingMode: false, pendingPost: null, returnToEditor: false, returnToTab: "library", preferredTab: undefined, linkingCellIndex: null, commissionId: null, commissionTitle: null, wipScript: null, bountyId: null, bountyTitle: null, workScriptId: null })
  },

  getPendingPost: () => {
    return get().pendingPost
  },

  consumePendingPost: () => {
    const post = get().pendingPost
    if (post) {
      // linkingCellIndex is consumed alongside the post (Gooncave reads it just
      // before calling this), so clear it here too — otherwise a stale cell
      // index survives into the next, unrelated link.
      set({ pendingPost: null, returnToEditor: false, returnToTab: undefined, linkingCellIndex: null })
    }
    return post
  },
}))
